import { useIntl } from 'react-intl';
import React from 'react';

import { formatNumberInput, resolveDraftResync } from '../utils/numberInput';

/**
 * Text state for a numeric input rendered as `type="text"`. Keeps the text as
 * typed (`175,` and `1,50` are legitimate intermediate spellings no number can
 * represent) and resyncs it only on a real external change — prefill, reset,
 * step buttons, language switch — via `resolveDraftResync`.
 *
 * `defaultValue` only seeds the initial text; a field that never receives a
 * defined `value` keeps its draft as the source of truth.
 */
export function useNumberDraft(value: unknown, defaultValue?: unknown) {
  const { locale } = useIntl();
  const [draft, setDraft] = React.useState(() =>
    formatNumberInput(value ?? defaultValue, locale),
  );
  const previousLocaleRef = React.useRef(locale);
  const hasBeenControlledRef = React.useRef(value !== undefined);

  React.useEffect(() => {
    const previousLocale = previousLocaleRef.current;
    previousLocaleRef.current = locale;
    if (value !== undefined) hasBeenControlledRef.current = true;

    const next = resolveDraftResync({
      hasBeenControlled: hasBeenControlledRef.current,
      previousLocale,
      draft,
      locale,
      value,
    });
    if (next !== null) setDraft(next);
  }, [value, draft, locale]);

  return { draft, setDraft, locale };
}
